import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Hand, Home } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  getVolunteerProfileByUserId,
  createVolunteerProfile,
} from '@/lib/firebase/queries/volunteerProfiles';
import { addUserRole } from '@/lib/firebase/queries/roles';
import { getUserData } from '@/lib/firebase/auth/user';
import { toast } from 'sonner';
import { VolunteerScopeNotice } from '@/components/auth/VolunteerScopeNotice';

export function VolunteerRegistrationPage() {
  const { t, dir, language } = useLanguage();
  const { currentUser, userData, setUserData } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form fields
  const [fullNameEn, setFullNameEn] = useState('');
  const [fullNameAr, setFullNameAr] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [nationalId, setNationalId] = useState('');
  const [scfhsNumber, setScfhsNumber] = useState('');
  const [employmentStatus, setEmploymentStatus] = useState('');
  const [volunteerAreas, setVolunteerAreas] = useState('');

  useEffect(() => {
    checkAuthAndProfile();
  }, [currentUser, userData]);

  const checkAuthAndProfile = async () => {
    if (!currentUser) {
      // Store the intended destination and redirect to login
      sessionStorage.setItem('redirectAfterLogin', '/vol');
      navigate('/login');
      return;
    }

    if (!userData?.id) {
      setLoading(true);
      return;
    }

    setLoading(true);
    try {
      // Check if user already has a volunteer profile
      const existingProfile = await getVolunteerProfileByUserId(userData.id);
      
      // Check if user already has volunteer role
      const hasVolunteerRole = userData.roles?.some(
        (role) => role.role === 'volunteer' && role.isActive
      );

      if (existingProfile || hasVolunteerRole) {
        // User is already registered as volunteer, add role if missing and redirect
        if (!hasVolunteerRole && currentUser?.uid) {
          try {
            await addUserRole(userData.id, 'volunteer', currentUser.uid);
            const refreshedUserData = await getUserData(currentUser.uid);
            if (refreshedUserData) {
              setUserData(refreshedUserData);
            }
          } catch (error) {
            console.error('Error adding volunteer role:', error);
          }
        }
        // Directly redirect to volunteer dashboard
        toast.success('مرحباً بك في لوحة تحكم المتطوع');
        navigate('/dashboard/volunteer');
        return;
      } else {
        // Pre-fill form with user data
        setFullNameEn(`${userData.firstNameEn || ''} ${userData.lastNameEn || ''}`.trim());
        setFullNameAr(`${userData.firstNameAr || ''} ${userData.lastNameAr || ''}`.trim());
        setEmail(userData.email || '');
        setPhone(userData.phone || '');
      }
    } catch (error) {
      console.error('Error checking profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!fullNameEn || !fullNameAr || !email || !phone || !nationalId || !employmentStatus) {
      toast.error(t('volunteerRegistration.errors.fillRequired') || 'Please fill all required fields');
      return;
    }

    // Validate phone number (10 digits starting with 05)
    if (!/^05\d{8}$/.test(phone)) {
      toast.error(t('volunteerRegistration.errors.invalidPhone') || 'Phone number must be 10 digits starting with 05');
      return;
    }

    // Validate national ID (10 digits)
    if (!/^\d{10}$/.test(nationalId)) {
      toast.error(t('volunteerRegistration.errors.invalidNationalId') || 'National ID must be 10 digits');
      return;
    }

    if (!userData?.id || !currentUser?.uid) {
      toast.error(t('volunteerRegistration.errors.authRequired') || 'Authentication required');
      return;
    }

    setSaving(true);
    try {
      // Create volunteer profile with both language names
      await createVolunteerProfile({
        userId: userData.id,
        fullName: language === 'ar' ? fullNameAr : fullNameEn,
        fullNameEn,
        fullNameAr,
        email,
        phone,
        nationalId,
        scfhsNumber: scfhsNumber || undefined,
        employmentStatus,
        volunteerAreas: volunteerAreas || undefined,
      });

      // Add volunteer role to user
      await addUserRole(userData.id, 'volunteer', currentUser.uid);

      // Refresh user data to get the new role
      const refreshedUserData = await getUserData(currentUser.uid);
      if (refreshedUserData) {
        setUserData(refreshedUserData);
        
        // Verify the volunteer role was successfully added
        const hasVolunteerRole = refreshedUserData.roles.some(
          (role) => role.role === 'volunteer' && role.isActive
        );
        
        if (hasVolunteerRole) {
          toast.success(t('volunteerRegistration.success.registered') || 'Volunteer registration completed successfully!');
          // Navigate to volunteer dashboard only if role was added
          navigate('/dashboard/volunteer');
        } else {
          toast.error(t('volunteerRegistration.errors.roleNotAdded') || 'Registration completed but volunteer role was not added. Please contact support.');
        }
      } else {
        toast.error(t('volunteerRegistration.errors.dataRefreshFailed') || 'Registration completed but could not refresh user data.');
      }
    } catch (error) {
      console.error('Error registering volunteer:', error);
      toast.error(t('volunteerRegistration.errors.registrationFailed') || 'Failed to complete registration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-secondary/10" dir={dir}>
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 bg-gradient-to-br from-primary/10 via-background to-secondary/10" dir={dir}>
      <div className="max-w-3xl mx-auto">
        {/* Back to Homepage Button */}
        <div className="mb-6">
            <Button
            onClick={() => navigate('/landing')}
              variant="outline"
            className="gap-2"
            >
            <Home className="h-4 w-4" />
            {t('auth.backToHomepage') || 'العودة للصفحة الرئيسية'}
            </Button>
            </div>

        {/* #72: volunteering ≠ beneficiary registration */}
        <VolunteerScopeNotice className="mb-6" />

                  <Card>
          <CardHeader className="text-center space-y-2">
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Hand className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-3xl font-bold">
              {t('volunteerRegistration.title') || 'Volunteer Registration'}
            </CardTitle>
            <CardDescription className="text-base">
              {t('volunteerRegistration.description') || 
                'Join our community and make a difference. Complete the form below to register as a volunteer.'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Full Name English */}
              <div className="space-y-2">
                <Label htmlFor="fullNameEn">
                  {t('volunteerRegistration.form.fullNameEn') || 'Full Name (English)'} *
                </Label>
                <Input
                  id="fullNameEn"
                  value={fullNameEn}
                  onChange={(e) => setFullNameEn(e.target.value)}
                  placeholder={t('volunteerRegistration.form.fullNameEnPlaceholder') || 'Enter your full name in English'}
                  required
                />
              </div>

              {/* Full Name Arabic */}
              <div className="space-y-2">
                <Label htmlFor="fullNameAr">
                  {t('volunteerRegistration.form.fullNameAr') || 'Full Name (Arabic)'} *
                </Label>
                <Input
                  id="fullNameAr"
                  value={fullNameAr}
                  onChange={(e) => setFullNameAr(e.target.value)}
                  placeholder={t('volunteerRegistration.form.fullNameArPlaceholder') || 'أدخل اسمك الكامل بالعربية'}
                  required
                  dir="rtl"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">
                  {t('volunteerRegistration.form.email') || 'Email Address'} *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="example@email.com"
                  required
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="phone">
                  {t('volunteerRegistration.form.phone') || 'Mobile Number'} *
                          </Label>
                          <Input
                  id="phone"
                            type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                            placeholder="05xxxxxxxx"
                            maxLength={10}
                  required
                          />
                <p className="text-xs text-muted-foreground">
                  {t('volunteerRegistration.form.phoneHelper') || 'Must be 10 digits starting with 05'}
                </p>
                        </div>

              {/* National ID */}
              <div className="space-y-2">
                    <Label htmlFor="nationalId">
                  {t('volunteerRegistration.form.nationalId') || 'National ID Number'} *
                    </Label>
                    <Input
                      id="nationalId"
                      value={nationalId}
                      onChange={(e) => setNationalId(e.target.value)}
                  placeholder={t('volunteerRegistration.form.nationalIdPlaceholder') || 
                    'Enter 10-digit National ID'}
                      maxLength={10}
                  required
                    />
                <p className="text-xs text-muted-foreground">
                  {t('volunteerRegistration.form.nationalIdHelper') || 'Must be 10 digits'}
                </p>
                  </div>

              {/* SCFHS Number */}
              <div className="space-y-2">
                    <Label htmlFor="scfhsNumber">
                  {t('volunteerRegistration.form.scfhsNumber') || 'SCFHS Specialty Number'}
                    </Label>
                    <Input
                      id="scfhsNumber"
                      value={scfhsNumber}
                      onChange={(e) => setScfhsNumber(e.target.value)}
                  placeholder={t('volunteerRegistration.form.scfhsNumberPlaceholder') || 
                    'Enter SCFHS number if applicable'}
                    />
                <p className="text-xs text-muted-foreground">
                  {t('volunteerRegistration.form.scfhsHelper') || 'Optional - for healthcare professionals'}
                </p>
                  </div>

              {/* Employment Status */}
              <div className="space-y-2">
                    <Label htmlFor="employmentStatus">
                  {t('volunteerRegistration.form.employmentStatus') || 'Employment Status'} *
                    </Label>
                <Select value={employmentStatus} onValueChange={setEmploymentStatus} required>
                      <SelectTrigger id="employmentStatus">
                        <SelectValue placeholder={
                      t('volunteerRegistration.form.selectEmploymentStatus') || 
                          'Select your employment status'
                        } />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employed">
                      {t('volunteerRegistration.form.employed') || 'Employed'}
                        </SelectItem>
                        <SelectItem value="unemployed">
                      {t('volunteerRegistration.form.unemployed') || 'Unemployed'}
                        </SelectItem>
                        <SelectItem value="student">
                      {t('volunteerRegistration.form.student') || 'Student'}
                        </SelectItem>
                        <SelectItem value="retired">
                      {t('volunteerRegistration.form.retired') || 'Retired'}
                        </SelectItem>
                        <SelectItem value="other">
                      {t('volunteerRegistration.form.other') || 'Other'}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

              {/* Volunteer Areas */}
              <div className="space-y-2">
                    <Label htmlFor="volunteerAreas">
                  {t('volunteerRegistration.form.volunteerAreas') || 'Preferred Volunteer Areas'}
                    </Label>
                    <Textarea
                      id="volunteerAreas"
                      value={volunteerAreas}
                      onChange={(e) => setVolunteerAreas(e.target.value)}
                  placeholder={t('volunteerRegistration.form.volunteerAreasPlaceholder') || 
                    'Tell us about your interests and areas where you would like to volunteer...'}
                  rows={4}
                />
                <p className="text-xs text-muted-foreground">
                  {t('volunteerRegistration.form.volunteerAreasHelper') || 
                    'Optional - help us match you with suitable volunteer opportunities'}
                </p>
                  </div>

              {/* Submit Button */}
              <div className="pt-4">
                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={saving}
                >
                  {saving ? (
                      <>
                        <Loader2 className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'} animate-spin`} />
                      {t('volunteerRegistration.form.submitting') || 'Submitting...'}
                      </>
                    ) : (
                      <>
                      <Hand className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                      {t('volunteerRegistration.form.submit') || 'Register as Volunteer'}
                      </>
                    )}
                  </Button>
              </div>

              {/* Required fields note */}
              <p className="text-sm text-muted-foreground text-center">
                {t('volunteerRegistration.form.requiredNote') || '* Required fields'}
              </p>
                </form>
              </CardContent>
            </Card>
      </div>
    </div>
  );
}

